import React, { useState, useEffect } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"

const AllocationDialog = ({
  focusedProduct,
  setFocusedProduct,
  focusedRow,
  setFocusedRow,
  salesReferenceData,
  executeUpdateSalesOrder,
}) => {
  const [allocations, setAllocations] = useState([])
  const [manualLot, setManualLot] = useState({
    LotSerialNo: "",
    ProductNo: "",
    Warehouse: "",
    Quantity: 0,
  })
  const [showOverAllocateDialog, setShowOverAllocateDialog] = useState(false)

  useEffect(() => {
    if (focusedProduct?.salesOrderItemLots) {
      setAllocations(focusedProduct.salesOrderItemLots)
    }
  }, [focusedProduct])

  const buildLotId = (Warehouse, ProductNo, LotSerialNo) =>
    `${Warehouse}${ProductNo}-${LotSerialNo}`

  const handleAddOrUpdateLot = (lot) => {
    setAllocations((prev) => {
      const idx = prev.findIndex((l) => l.id === lot.id)
      const updated = [...prev]
      if (idx >= 0) updated[idx] = lot
      else updated.push(lot)
      return updated
    })
  }

  const handleRemoveLot = (lotId) => {
    setAllocations((prev) => prev.filter((l) => l.id !== lotId))
  }

  const handleManualAdd = () => {
    const { LotSerialNo, ProductNo, Warehouse, Quantity } = manualLot
    const lotId = buildLotId(Warehouse, ProductNo, LotSerialNo)

    const isDuplicate = allocations.some((l) => l.id === lotId)
    if (isDuplicate) {
      alert("This lot is already allocated.")
      return
    }

    const newLot = {
      id: lotId,
      salesOrderItemId: focusedProduct.id,
      warehouse: Warehouse,
      lot: LotSerialNo,
      quantity: Number(Quantity),
      type: ProductNo.slice(-4, -2) === "01" ? "BOTTLE" : "CASE",
    }

    handleAddOrUpdateLot(newLot)

    setManualLot({
      LotSerialNo: "",
      ProductNo: "",
      Warehouse: "",
      Quantity: 0,
    })
  }

  const handleSave = async () => {
    const totalRequested =
      focusedProduct.cases > 0 ? focusedProduct.cases : focusedProduct.bottles
    const totalAllocated = allocations.reduce((sum, l) => sum + l.quantity, 0)

    if (totalAllocated > totalRequested) {
      setShowOverAllocateDialog(true)
      return
    }

    await submitAllocations()
  }

  const submitAllocations = async () => {
    const updatedOrder = await executeUpdateSalesOrder(
      "POST",
      `/api/sales/update/${focusedRow.id}`,
      {
        salesOrderItemLots: allocations,
      }
    )

    if (updatedOrder?.error) {
      alert("Failed to update sales order.")
      return
    }

    setFocusedRow(updatedOrder)

    const updatedProduct = updatedOrder.salesOrderItems.find(
      (item) => item.id === focusedProduct.id
    )

    const matchingLots =
      salesReferenceData?.inventoryOnHand?.filter((lot) => {
        const baseSku = updatedProduct.sku?.slice(0, -4)
        const suffix = updatedProduct.sku?.slice(-2)
        const lotBase = lot.ProductNo?.slice(0, -4)
        const lotSuffix = lot.ProductNo?.slice(-2)
        return lotBase === baseSku && lotSuffix === suffix
      }) || []

    setFocusedProduct({
      ...updatedProduct,
      matchingLots,
    })

    setFocusedProduct(null)
  }

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        open={!!focusedProduct}
        onClose={() => setFocusedProduct(null)}
      >
        <Box p={3}>
          <Typography variant="h6" textAlign="center" mb={2}>
            Product Allocation – {focusedProduct?.sku} Requested:{" "}
            {focusedProduct?.cases > 0
              ? focusedProduct?.cases
              : focusedProduct?.bottles}
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Manual Allocation
            </Typography>
            <Stack spacing={2}>
              {["LotSerialNo", "ProductNo", "Warehouse", "Quantity"].map(
                (key) => (
                  <TextField
                    key={key}
                    label={key}
                    type={key === "Quantity" ? "number" : "text"}
                    size="small"
                    value={manualLot[key]}
                    onChange={(e) =>
                      setManualLot((prev) => ({
                        ...prev,
                        [key]:
                          key === "Quantity"
                            ? Number(e.target.value)
                            : e.target.value,
                      }))
                    }
                  />
                )
              )}
              <Button
                variant="contained"
                color="warning"
                onClick={handleManualAdd}
              >
                Add Manual Lot
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Suggested Lots
            </Typography>
            <Stack spacing={1}>
              {focusedProduct?.matchingLots?.map((lot) => {
                const lotId = buildLotId(
                  lot.Warehouse,
                  lot.ProductNo,
                  lot.LotSerialNo
                )
                const isAllocated = allocations.find((l) => l.id === lotId)
                const isBottleOrder = focusedProduct?.bottles > 0

                return (
                  <Box
                    key={lotId}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    bgcolor={isAllocated ? "#f0f9ff" : "inherit"}
                    p={1}
                    borderRadius={1}
                  >
                    <Typography variant="body2" flex={1}>
                      Lot: {lot.LotSerialNo} – SKU: {lot.ProductNo} – WH:{" "}
                      {lot.Warehouse} – OnHand: {lot.OnHand}
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      sx={{ width: 100 }}
                      value={isAllocated?.quantity || 0}
                      onChange={(e) => {
                        const quantity = Math.min(
                          Number(e.target.value),
                          lot.OnHand
                        )
                        const newLot = {
                          id: lotId,
                          salesOrderItemId: focusedProduct.id,
                          warehouse: lot.Warehouse,
                          quantity,
                          lot: lot.LotSerialNo,
                          type: isBottleOrder ? "BOTTLE" : "CASE",
                        }
                        if (quantity > 0) handleAddOrUpdateLot(newLot)
                        else handleRemoveLot(lotId)
                      }}
                    />
                  </Box>
                )
              })}
            </Stack>
          </Paper>

          {allocations.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Current Allocations
              </Typography>
              <Stack spacing={1}>
                {allocations.map((lot) => (
                  <Stack
                    key={lot.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      • Lot: <strong>{lot.lot}</strong> – Type:{" "}
                      <strong>{lot.type}</strong> – WH:{" "}
                      <strong>{lot.warehouse}</strong> – Qty:{" "}
                      <strong>{lot.quantity}</strong>
                    </Typography>
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveLot(lot.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Done
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setFocusedProduct(null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ⚠️ Over-Allocation Warning Popup */}
      <Dialog
        open={showOverAllocateDialog}
        onClose={() => setShowOverAllocateDialog(false)}
      >
        <DialogTitle>Over-Allocation Warning</DialogTitle>
        <DialogContent>
          <Typography>
            The total allocated quantity exceeds the requested amount. Are you
            sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowOverAllocateDialog(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowOverAllocateDialog(false)
              submitAllocations()
            }}
            color="primary"
            variant="contained"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AllocationDialog
